import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSession } from '@/lib/session';
import { hasPermission } from '@/lib/permissions';
import PermissionRequiredBanner from '@/components/PermissionRequiredBanner';
import Icon from '@/components/Icon';
import {
  getInProgressCycle,
  listHistoryCycles,
  startCycle,
  formatCycleRange,
  formatRelativeTime,
  type ReconciliationCycleRow,
} from '@/lib/reconciliation';

// ============================================================
// ReconciliationPage — Phase 3, Epic 3B (landing)
// ============================================================
// Entry surface for the reconciliation flow. Renders one of:
//
//   1. In-progress card  — when a cycle exists with status
//      in_progress for the active clinic. "Resume count"
//      button → /reconciliation/:id walk-through.
//
//   2. Start card        — when no cycle is open. "Start
//      count" button → calls start RPC, then navigates to
//      the new cycle.
//
//   3. History strip     — submitted/cancelled cycles, newest
//      first. Always visible underneath the active-state card.
//      Each row links to /reconciliation/:id read-only view.
//
// Permission gate: reconciliation.view_history is the bare-
// minimum to render anything here (today any_member per
// posture). Without it: PermissionRequiredBanner.
//
// Start button gated separately on reconciliation.start.
//
// No bottom-nav entry — entry point is a button on the
// Inventory page (build plan "Inventory → Admin menu"). Direct
// nav to /reconciliation works for deep-linking from history
// or for re-entry mid-cycle.
// ============================================================

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | {
      kind: 'loaded';
      inProgress: ReconciliationCycleRow | null;
      history: ReconciliationCycleRow[];
    };

export default function ReconciliationPage() {
  const session = useSession();
  const navigate = useNavigate();
  const clinicId = session.activeMembership.clinic_id;

  const canView = hasPermission(session, 'reconciliation.view_history');
  const canStart = hasPermission(session, 'reconciliation.start');

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const [inProgress, history] = await Promise.all([
        getInProgressCycle(clinicId),
        listHistoryCycles(clinicId, 20),
      ]);
      setState({ kind: 'loaded', inProgress, history });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load reconciliation state.';
      setState({ kind: 'error', message });
    }
  }, [clinicId]);

  useEffect(() => {
    if (!canView) return;
    void refresh();
  }, [canView, refresh]);

  const onStart = useCallback(async () => {
    if (!canStart) return;
    setStarting(true);
    setStartError(null);
    try {
      const id = await startCycle(clinicId);
      navigate(`/reconciliation/${id}`);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to start cycle.';
      setStartError(message);
      setStarting(false);
    }
  }, [canStart, clinicId, navigate]);

  if (!canView) {
    return (
      <div className="p-4 pb-6">
        <PermissionRequiredBanner
          permission="reconciliation.view_history"
          message="Reconciliation is restricted at this clinic. Ask your clinic admin if you need access."
        />
      </div>
    );
  }

  if (state.kind === 'loading') {
    return (
      <div className="p-4 pb-6">
        <PageHeader />
        <div className="text-sm text-slate-500 px-1">Loading…</div>
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div className="p-4 pb-6">
        <PageHeader />
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {state.message}
        </div>
      </div>
    );
  }

  const { inProgress, history } = state;

  return (
    <div className="p-4 pb-6 space-y-4">
      <PageHeader />

      {inProgress ? (
        <ResumeCard
          cycle={inProgress}
          onOpen={() => navigate(`/reconciliation/${inProgress.id}`)}
        />
      ) : (
        <StartCard
          canStart={canStart}
          starting={starting}
          error={startError}
          onStart={onStart}
        />
      )}

      <HistorySection cycles={history} />
    </div>
  );
}

// ---- Page chrome --------------------------------------------------

function PageHeader() {
  return (
    <div className="px-1">
      <Link
        to="/inventory"
        aria-label="Back to inventory"
        className="inline-flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-slate-900 -ml-1 mb-1"
      >
        <Icon name="chevright" className="w-4 h-4 rotate-180" />
        Back
      </Link>
      <h1 className="text-xl font-bold text-slate-900">Reconciliation</h1>
      <p className="text-sm text-slate-500 mt-0.5">
        Physical count of essential supplies.
      </p>
    </div>
  );
}

// ---- Resume (in-progress) card ------------------------------------

function ResumeCard({
  cycle,
  onOpen,
}: {
  cycle: ReconciliationCycleRow;
  onOpen: () => void;
}) {
  return (
    <div className="rounded-2xl border-2 border-amber-300 bg-amber-50/60 p-4">
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-amber-200/60 p-2 flex-shrink-0">
          <Icon name="clock" className="w-5 h-5 text-amber-700" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-700">
            Count in progress
          </div>
          <div className="text-base font-semibold text-slate-900 mt-0.5">
            Started {formatRelativeTime(cycle.started_at)}
          </div>
          <div className="text-xs text-slate-600 mt-0.5">
            {formatCycleRange(cycle)}
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={onOpen}
        className="mt-3 w-full rounded-xl bg-amber-600 text-white font-semibold py-2.5 hover:bg-amber-700 active:bg-amber-800 transition"
      >
        Resume count
      </button>
    </div>
  );
}

// ---- Start card ---------------------------------------------------

function StartCard({
  canStart,
  starting,
  error,
  onStart,
}: {
  canStart: boolean;
  starting: boolean;
  error: string | null;
  onStart: () => void;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-green-50 p-2 flex-shrink-0">
          <Icon name="check" className="w-5 h-5 text-green-700" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-base font-semibold text-slate-900">
            Start a new count
          </div>
          <div className="text-xs text-slate-600 mt-0.5 leading-snug">
            Snapshots current essential supplies as a baseline, then walks you
            through entering actual shelf counts. Resumable across sessions —
            you can pause and come back.
          </div>
        </div>
      </div>
      {error && (
        <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-800">
          {error}
        </div>
      )}
      <button
        type="button"
        onClick={onStart}
        disabled={!canStart || starting}
        className="mt-3 w-full rounded-xl bg-action text-white font-semibold py-2.5 hover:bg-action-hover active:bg-action-active disabled:bg-slate-300 disabled:cursor-not-allowed transition"
      >
        {starting ? 'Starting…' : canStart ? 'Start count' : 'Permission required'}
      </button>
    </div>
  );
}

// ---- History section ----------------------------------------------

function HistorySection({
  cycles,
}: {
  cycles: readonly ReconciliationCycleRow[];
}) {
  return (
    <div>
      <div className="px-1 mb-2">
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          Recent
        </h2>
      </div>
      {cycles.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white p-4 text-sm text-slate-500 text-center">
          No prior reconciliations yet.
        </div>
      ) : (
        <div className="space-y-2">
          {cycles.map((c) => (
            <HistoryRow key={c.id} cycle={c} />
          ))}
        </div>
      )}
    </div>
  );
}

function HistoryRow({ cycle }: { cycle: ReconciliationCycleRow }) {
  const wasCancelled = cycle.status === 'cancelled';

  return (
    <Link
      to={`/reconciliation/${cycle.id}`}
      className="block rounded-xl border border-slate-200 bg-white p-3 hover:border-slate-300 transition"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="text-sm font-semibold text-slate-900">
              {formatCycleRange(cycle)}
            </div>
            {wasCancelled && (
              <span className="text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 bg-slate-100 text-slate-600">
                Cancelled
              </span>
            )}
          </div>
          {!wasCancelled && cycle.totals_in_scope !== null && (
            <div className="text-xs text-slate-600 mt-0.5">
              {cycle.totals_counted}/{cycle.totals_in_scope} counted
              {cycle.totals_skipped! > 0 && ` · ${cycle.totals_skipped} skipped`}
              {cycle.totals_with_delta! > 0 &&
                ` · ${cycle.totals_with_delta} adjusted`}
            </div>
          )}
        </div>
        <Icon name="chevright" className="w-4 h-4 text-slate-400 flex-shrink-0" />
      </div>
    </Link>
  );
}
